import React, { createContext, useState, useCallback, useContext } from "react";

// GEOMETRY HELPERS
interface Point {
	x: number;
	y: number;
	time: number; // TimeStamp
}

function calculateVectorProperties(
	point1: Point | undefined,
	point2: Point | undefined
):
	| { magnitude: number; directionDegrees: number; duration: number }
	| undefined {
	if (!point1 || !point2) return undefined;

	const dx = point2.x - point1.x;
	const dy = point2.y - point1.y;
	const dt = point2.time - point1.time;

	const magnitude = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
	const angleRadians = Math.atan2(dy, dx);
	const directionDegrees = angleRadians * (180 / Math.PI);
	const duration = dt;

	return { magnitude, directionDegrees, duration };
}

// CONTEXT TYPES AND CREATION
interface SwipeContextType {
	registerSwipeHandlers: (
		onLeftSwipe: () => void,
		onRightSwipe: () => void
	) => void;
	// You might want to add more context values in the future
	swipeThreshold: number;
	velocityThreshold: number;
}

// Create and export the context
export const SwipeContext = createContext<SwipeContextType | null>(null);

// CUSTOM HOOK - Export this for child components
export const useSwipeActions = () => {
	const context = useContext(SwipeContext);

	if (!context) {
		throw new Error("useSwipeActions must be used within a SwipeProvider");
	}

	return context;
};

// PROVIDER PROPS
interface SwipeProviderProps {
	swipeThreshold?: number;
	velocityThreshold?: number;
	children?: React.ReactNode;
}

// SWIPE PROVIDER COMPONENT
export const SwipeProvider: React.FC<SwipeProviderProps> = ({
	swipeThreshold = 12, // minimum distance in pixels to trigger swipe
	velocityThreshold = 0.25, // minimum velocity to trigger swipe
	children
}) => {
	const [leftSwipeHandler, setLeftSwipeHandler] = useState<(() => void) | null>(
		null
	);
	const [rightSwipeHandler, setRightSwipeHandler] = useState<
		(() => void) | null
	>(null);

	// Use useCallback with proper dependencies to avoid stale closures
	const initialTouchPoints = React.useRef(new Map<number, Point>()).current;

	const registerSwipeHandlers = useCallback(
		(onLeftSwipe: () => void, onRightSwipe: () => void) => {
			setLeftSwipeHandler(() => onLeftSwipe);
			setRightSwipeHandler(() => onRightSwipe);
		},
		[]
	);

	const handleTouchStart = useCallback(
		(e: React.TouchEvent<HTMLDivElement>) => {
			for (let i = 0; i < e.targetTouches.length; i++) {
				const touch = e.targetTouches[i];
				if (!initialTouchPoints.has(touch.identifier)) {
					initialTouchPoints.set(touch.identifier, {
						x: touch.clientX,
						y: touch.clientY,
						time: e.timeStamp
					});
				}
			}
		},
		[initialTouchPoints]
	);

	const handleTouchEnd = useCallback(
		(e: React.TouchEvent<HTMLDivElement>) => {
			for (let i = 0; i < e.changedTouches.length; i++) {
				const touch = e.changedTouches[i];
				const endPoint = {
					x: touch.clientX,
					y: touch.clientY,
					time: e.timeStamp
				};

				try {
					const initialPoint = initialTouchPoints.get(touch.identifier);
					const vector = calculateVectorProperties(initialPoint, endPoint);

					if (vector) {
						const velocity = vector.magnitude / vector.duration;
						//console.log(`Velocity: ${velocity}, Vector:`, vector);

						// Logic for Swipe
						if (
							vector.magnitude >= swipeThreshold &&
							velocity >= velocityThreshold
						) {
							//Then we have a valid swipe.
							//https://dr282zn36sxxg.cloudfront.net/datastreams/f-d%3A4dad0f91135a66c1c4d5af3b16f02b93914953d0a5852955ed807654%2BIMAGE_TINY%2BIMAGE_TINY.1
							//https://stackoverflow.com/questions/17574424/how-to-use-atan2-in-combination-with-other-radian-angle-systems
							const posDegrees = Math.abs(vector.directionDegrees);

							if (0 <= posDegrees && posDegrees <= 22.5) {
								console.log(`SwipeProvider: RightSwipe`);
								rightSwipeHandler?.();
							} else if (157.5 <= posDegrees && posDegrees <= 180) {
								console.log(`SwipeProvider: LeftSwipe`);
								leftSwipeHandler?.();
							} else {
								console.log(
									`SwipeProvider: ID: ${touch.identifier} => Not a x-axis Swipe [dir:${vector.directionDegrees}}] `
								);
							}
						} else {
							console.log(
								`SwipeProvider: ID: ${touch.identifier} => Not a Swipe (too short [is mag: ${vector.magnitude} >= ${swipeThreshold}] or too slow [is vel: ${velocity} >= ${velocityThreshold}])`
							);
						}
					}
				} finally {
					initialTouchPoints.delete(touch.identifier);
				}
			}
		},
		[
			swipeThreshold,
			velocityThreshold,
			leftSwipeHandler,
			rightSwipeHandler,
			initialTouchPoints
		]
	);

	const contextValue: SwipeContextType = {
		registerSwipeHandlers,
		swipeThreshold,
		velocityThreshold
	};

	return (
		<SwipeContext.Provider value={contextValue}>
			<div
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
				style={{ width: "100%", height: "100%" }}
			>
				{children}
			</div>
		</SwipeContext.Provider>
	);
};

// OPTIONAL: Export types for consumers
export type { SwipeContextType, SwipeProviderProps };
